import { useState, useTransition, type ReactNode } from "react";
import { useNavigate as useRouter } from "../../router";
import { Button } from "../../button";
import { Card, CardContent } from "../../card";
import { Input } from "../../input";
import { Textarea } from "../../textarea";
import { SettingsSection } from "./settings-section";

export interface TemplateRow {
  id: number;
  name: string;
  subject: string | null;
  body: string;
}

export interface TemplateEditorProps {
  title?: ReactNode;
  description?: ReactNode;
  templates: TemplateRow[];
  /** Whether the subject field is shown (e.g. SMS templates may not have one). */
  withSubject?: boolean;
  itemNoun?: string;
  onCreate: (input: {
    name: string;
    subject: string;
    body: string;
  }) => Promise<void>;
  onUpdate: (
    id: number,
    patch: Partial<{ name: string; subject: string; body: string }>,
  ) => Promise<void>;
  onArchive: (id: number) => Promise<void>;
}

export function TemplateEditor({
  title = "Templates",
  description,
  templates,
  withSubject = true,
  itemNoun = "template",
  onCreate,
  onUpdate,
  onArchive,
}: TemplateEditorProps) {
  const router = useRouter();
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [draft, setDraft] = useState({ name: "", subject: "", body: "" });

  function run<T>(fn: () => Promise<T>) {
    setError(null);
    startTransition(async () => {
      try {
        await fn();
        router.refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Action failed.");
      }
    });
  }

  return (
    <SettingsSection title={title} description={description}>
      <div className="flex flex-col gap-3">
        {templates.length === 0 ? (
          <div className="type-body text-muted-foreground">
            No {itemNoun}s yet. Add one below.
          </div>
        ) : (
          <ul className="flex flex-col">
            {templates.map((t) => {
              const open = openId === t.id;
              return (
                <li
                  key={t.id}
                  className="flex flex-col gap-2 border-b border-border/40 py-3 first:pt-0 last:border-b-0 last:pb-0"
                >
                  <div className="flex items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => setOpenId(open ? null : t.id)}
                      className="flex flex-1 items-baseline gap-2 text-left"
                    >
                      <span className="type-body text-foreground">{t.name}</span>
                      {withSubject && t.subject ? (
                        <span className="type-body text-muted-foreground">
                          · {t.subject}
                        </span>
                      ) : null}
                    </button>
                    <Button
                      size="xs"
                      variant="ghost"
                      disabled={pending}
                      onClick={() => run(() => onArchive(t.id))}
                    >
                      Remove
                    </Button>
                  </div>
                  {open ? (
                    <Card className="gap-3 py-3">
                      <CardContent className="space-y-2 px-3">
                        <Input
                          defaultValue={t.name}
                          onBlur={(e) => {
                            if (e.target.value !== t.name) {
                              run(() =>
                                onUpdate(t.id, { name: e.target.value }),
                              );
                            }
                          }}
                        />
                        {withSubject ? (
                          <Input
                            defaultValue={t.subject ?? ""}
                            placeholder="Subject"
                            onBlur={(e) => {
                              if (e.target.value !== (t.subject ?? "")) {
                                run(() =>
                                  onUpdate(t.id, {
                                    subject: e.target.value,
                                  }),
                                );
                              }
                            }}
                          />
                        ) : null}
                        <Textarea
                          defaultValue={t.body}
                          rows={6}
                          onBlur={(e) => {
                            if (e.target.value !== t.body) {
                              run(() =>
                                onUpdate(t.id, { body: e.target.value }),
                              );
                            }
                          }}
                        />
                      </CardContent>
                    </Card>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}

        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-2 gap-2">
            <Input
              placeholder={`New ${itemNoun} name`}
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
            {withSubject ? (
              <Input
                placeholder="Subject"
                value={draft.subject}
                onChange={(e) =>
                  setDraft({ ...draft, subject: e.target.value })
                }
              />
            ) : null}
          </div>
          <Textarea
            placeholder="Body"
            rows={4}
            value={draft.body}
            onChange={(e) => setDraft({ ...draft, body: e.target.value })}
          />
          <div className="flex justify-end">
            <Button
              size="sm"
              disabled={pending || !draft.name || !draft.body}
              onClick={() => {
                const snapshot = draft;
                run(async () => {
                  await onCreate(snapshot);
                  setDraft({ name: "", subject: "", body: "" });
                });
              }}
            >
              Add {itemNoun}
            </Button>
          </div>
        </div>

        {error ? <p className="type-body text-destructive">{error}</p> : null}
      </div>
    </SettingsSection>
  );
}
